<script lang="ts">
	import { yyyy, zzzz } from "../../store.svelte";
	import { toast } from "../../lib/toast.svelte.ts";
	import { tick } from "svelte";

	interface Props {
		energized: boolean;
		lw_id: number;
		feederid: string;
	}

	let { energized, lw_id, feederid }: Props = $props();
	let waiting: boolean = $state(false);
	let localEnergized: boolean = $state(energized);
	
	// Update local state when prop changes
	$effect(() => {
		localEnergized = energized;
	});

	const url: string = yyyy.uri.api;
	
	// Check if current user is the owner
	const isOwner = yyyy.sesh.tokeninfo?.email === yyyy.sesh.activeLivewire?.owner;

	let powerControl = async (lw_id, newEnergizedState) => {
		waiting = true;

		let _body = JSON.stringify({
			lw_id: lw_id,
			livewire: yyyy.sesh.activeLivewire.wireident,
			energ: newEnergizedState,
		});

		let _hdrs = {
			"Content-Type": "application/json",
			Authorization: "Bearer " + yyyy.sesh.logtoken.token,
		};
		let fetchopts = { method: "post", body: _body, headers: _hdrs };
		
		try {
			const response = await fetch(`${url}/rpc/power_control`, fetchopts);
			const data = await response.json();
			
			if (response.ok) {
				console.log(data);
				// Successfully changed state
				localEnergized = newEnergizedState;
				await tick();
				zzzz.emit("redraw");
				toast.success(`Device ${newEnergizedState ? 'energized' : 'de-energized'} successfully`);
			} else {
				console.log(data);
				// Failed to change state, revert the checkbox
				localEnergized = !newEnergizedState;
				toast.warning(data.message || 'Failed to change device state');
				if (data.details) {
					toast.warning(data.details);
				}
			}
		} catch (error) {
			console.error('Power control error:', error);
			// Revert on error
			localEnergized = !newEnergizedState;
			toast.error('Failed to communicate with server');
		} finally {
			waiting = false;
		}
		
		zzzz.emit("redraw");
	};
</script>

<div class="card-content" style="padding: 8.775px">
	<h2 class="subtitle" style="text-align: center;">Device Operation</h2>
	<p class="content">
		This device is currently {#if waiting}being{/if}
		{#if !localEnergized}de{/if}energized. 
		{#if isOwner && !waiting}
			Toggle the switch below to change its state.
		{:else if !isOwner}
			<br><br>
			<span style="color: #ff3e00; font-weight: 500;">
				Only the livewire owner can energize or de-energize devices.
			</span>
			<br>
			<span style="color: #666; font-size: 0.9em;">
				If you want to make changes, you can fork this livewire as your own.
			</span>
		{/if}
	</p>
	<div class="power-control-container">
		<label class="toggle-container">
			<span class="toggle-label">
				{localEnergized ? 'Energized' : 'De-energized'}
			</span>
			<input
				type="checkbox"
				bind:checked={localEnergized}
				onchange={() => {
					powerControl(lw_id, localEnergized);
				}}
				disabled={!isOwner || waiting || (!feederid && localEnergized)}
			/>
			<span class="toggle-switch" class:is-loading={waiting}></span>
		</label>
	</div>
	{#if waiting}
		<progress
			style="height: 2px;"
			class="progress is-small is-primary"
			max="100"
		/>
	{/if}
</div>

<style>
	.card-content {
		padding-bottom: 0.75rem;
	}

	.power-control-container {
		display: flex;
		justify-content: center;
		align-items: center;
		margin-top: 1rem;
		padding-bottom: 1rem;
	}

	.toggle-container {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		cursor: pointer;
		user-select: none;
		min-width: 180px;
	}

	.toggle-label {
		font-weight: 500;
		color: #4a4a4a;
		flex: 1;
		text-align: left;
	}

	.toggle-container input[type="checkbox"] {
		opacity: 0;
		width: 0;
		height: 0;
		position: absolute;
	}

	.toggle-switch {
		position: relative;
		display: inline-block;
		width: 50px;
		height: 24px;
		background-color: #ccc;
		border-radius: 4px;
		transition: background-color 0.3s;
		flex-shrink: 0;
	}

	.toggle-switch::after {
		content: '';
		position: absolute;
		width: 18px;
		height: 18px;
		left: 3px;
		top: 3px;
		background-color: white;
		border-radius: 4px;
		transition: transform 0.3s;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
	}

	input[type="checkbox"]:checked + .toggle-switch {
		background-color: #ff3e00;
	}

	input[type="checkbox"]:checked + .toggle-switch::after {
		transform: translateX(26px);
	}

	input[type="checkbox"]:disabled + .toggle-switch {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.toggle-switch.is-loading {
		animation: pulse 1s infinite;
	}

	@keyframes pulse {
		0%, 100% {
			opacity: 1;
		}
		50% {
			opacity: 0.6;
		}
	}

	.toggle-container:hover .toggle-switch:not(:disabled) {
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
	}

	.content {
		text-align: center;
	}

	.subtitle {
		margin-bottom: 0.75rem;
	}
</style>
